import * as React from "react";
import { useToasts } from "react-toast-notifications";
import styles from "../ExportActiveClients/ExportActiveClients.module.scss";
import { cn } from "../../../../helpers";
import Alert from "../../../bits/Alert/Alert";
import { Button } from "../../../bits/Button/Button";
import { fxs } from "../../../../../utils/firestore";
import { httpsCallable } from "firebase/functions";

interface RecheckExportResult {
    sheet_id?: string;
    csv_path?: string;
    total_exported: number;
}

export default function ExportRecheckList() {
    const { addToast } = useToasts();
    const [isExporting, setIsExporting] = React.useState(false);
    const [progress, setProgress] = React.useState(5);
    const [exportStatus, setExportStatus] = React.useState<
        "idle" | "success" | "error"
    >("idle");
    const [displayMessage, setDisplayMessage] = React.useState<string | JSX.Element>(
        ""
    );

    const getMessage = (exportLink: string) => (
        <p>
            Export completed. Please click the link to see the list{" "}
            <a href={exportLink} target="_blank" rel="noreferrer">
                {exportLink}
            </a>
        </p>
    );

    const getLocalMessage = (csvPath: string, totalExported: number) => (
        <p>
            Local export completed with {totalExported} rows. CSV path: {csvPath}
        </p>
    );

    return (
        <div className={styles.container}>
            <p className={styles.title}>Export recheck list</p>
            <p className={styles.description}>
                Click on the button below to export a new recheck list. On
                completion, you will have the link to the exported list.
            </p>

            <div
                className={cn(styles.progressBar, {
                    [styles.error]: exportStatus === "error",
                })}
            >
                <div
                    className={cn(styles.progressFill, {
                        [styles.error]: exportStatus === "error",
                    })}
                    style={{
                        width: `${progress}%`,
                    }}
                />
            </div>

            {exportStatus === "success" && (
                <Alert type="info">{displayMessage}</Alert>
            )}

            <div className={styles.buttonContainer}>
                <Button
                    onClick={async () => {
                        setIsExporting(true);
                        setProgress(0);
                        setExportStatus("idle");

                        const progressInterval = setInterval(() => {
                            setProgress((prev) => {
                                if (prev >= 90) {
                                    clearInterval(progressInterval);
                                    return 90;
                                }
                                return prev + 10;
                            });
                        }, 500);

                        const exportRecheckList = httpsCallable<
                            void,
                            RecheckExportResult
                        >(fxs, "httpFunctionExportClientsRecheckList");

                        try {
                            const result = await exportRecheckList();
                            setProgress(100);
                            clearInterval(progressInterval);
                            setExportStatus("success");
                            if (result.data.sheet_id) {
                                const exportLink = `https://docs.google.com/spreadsheets/d/${result.data.sheet_id}`;
                                setDisplayMessage(getMessage(exportLink));
                            } else if (result.data.csv_path) {
                                setDisplayMessage(
                                    getLocalMessage(
                                        result.data.csv_path,
                                        result.data.total_exported
                                    )
                                );
                            } else {
                                throw new Error("Export completed without a result");
                            }
                        } catch (ex) {
                            clearInterval(progressInterval);
                            setExportStatus("error");

                            addToast(
                                "Failed to export clients, please contact your administrator.",
                                {
                                    appearance: "error",
                                }
                            );
                        } finally {
                            setIsExporting(false);
                        }
                    }}
                    disabled={isExporting}
                >
                    {isExporting && "Exporting..."}
                    {!isExporting && exportStatus === "success" && "Export Complete"}
                    {!isExporting && exportStatus !== "success" && "Run export"}
                </Button>
            </div>
        </div>
    );
}
